import {range} from '@benchling/js-utils';
import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {removeGaps} from 'biojs/Util';
import {AntibodyNumberingLayout} from 'constants/MapsConstants';
import {getRealToGapMap} from 'molbio/aa-sequences/alignments/getRealToGapMap';
import AntibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {
  NumberingScheme,
  ValidNumberingResult,
} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {getMetadataSummary} from 'molbio/antibody-engineering/useAntibodyMetadata';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import Type from 'molbio/maps/Type';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHTEST} from 'ui-library/Colors';

export const MIN_WIDTH_FOR_INDEX = 21;

/**
 * Antibody numbering for a stretch of residues.
 */
export interface ResidueNumbering {
  // Ungapped index of the residue the first label belongs to
  start: number;
  // One label per residue from `start`, or null for a residue that has no number
  labels: ReadonlyArray<string | null>;
  // Shown on hover over the numbered regions, e.g. "Human Heavy"
  summary: string;
}

interface NumberedRegion {
  // Ungapped residue indices; end is exclusive
  start: number;
  end: number;
}

/**
 * Splits numbering into the regions of consecutive numbered residues, skipping residues with no number.
 */
export function getNumberedRegions(numbering: ResidueNumbering): Array<NumberedRegion> {
  const regions: Array<NumberedRegion> = [];
  let regionStart: number | null = null;
  numbering.labels.forEach((label, i) => {
    if (label != null && regionStart == null) {
      regionStart = i;
    } else if (label == null && regionStart != null) {
      regions.push({start: numbering.start + regionStart, end: numbering.start + i});
      regionStart = null;
    }
  });
  if (regionStart != null) {
    regions.push({start: numbering.start + regionStart, end: numbering.start + numbering.labels.length});
  }
  return regions;
}

interface AntibodyNumberProps {
  value: string;
  gappedIndex: number;
  xFn: TrackProps['xFn'];
  layout: TrackProps['layout'];
}

/**
 * Renders a single antibody index at the correct horizontal position
 */
function AntibodyNumber(props: AntibodyNumberProps): JSX.Element {
  const {value, gappedIndex, xFn, layout} = props;
  const xPos = xFn(gappedIndex);

  const type = new Type(layout.type.fontSize);
  const totalWidth = xFn(gappedIndex + 1) - xPos;
  const textWidth = type.width(value.length);
  const xPad = (totalWidth - textWidth) / 2;

  return (
    <text
      data-gapped-index={gappedIndex}
      transform={`translate(${xPos + xPad} 6)`}
      {...getTestProps({component: 'AntibodyNumbering', element: 'number', key: value})}
      data-amp-safe={false}
    >
      {value}
    </text>
  );
}

interface AntibodyNumberedRegionProps {
  layout: AntibodyNumberingLayout;
  numbering: ResidueNumbering;
  onAlignment: boolean;
  realToGapped: Array<number>;
  region: NumberedRegion;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

function AntibodyNumberedRegion(props: AntibodyNumberedRegionProps): JSX.Element | null {
  const {layout, numbering, onAlignment, realToGapped, region, setSelection, sliceStart, sliceEnd, xFn} =
    props;

  // An alignment has a single slice spanning every residue. A sequence map draws only the slice's residues.
  const firstResidue = onAlignment ? region.start : Math.max(region.start, sliceStart);
  const endResidue = onAlignment ? region.end : Math.min(region.end, sliceEnd);
  if (firstResidue >= endResidue) {
    return null;
  }
  const toSliceIndex = (residueIndex: number): number =>
    onAlignment ? realToGapped[residueIndex] : residueIndex;
  const regionStart = toSliceIndex(firstResidue);
  const regionEnd = toSliceIndex(endResidue - 1) + 1;

  // Cannot be replaced with xFn(1) - xFn(0) because xFn is specific to the slice and may not have 1 and 0 in its domain
  const zoomedIn = xFn(regionStart + 1) - xFn(regionStart) > MIN_WIDTH_FOR_INDEX;
  const tooltip = zoomedIn
    ? numbering.summary
    : onAlignment
      ? 'Zoom in using the minimap to see antibody numbering'
      : 'Zoom in to see antibody numbering';

  return (
    <>
      <Tooltip label={tooltip} withinPortal={true} disabled={tooltip === ''}>
        <rect
          onClick={() => {
            setSelection(toSliceIndex(region.start), toSliceIndex(region.end - 1) + 1);
          }}
          className={`layer-fill ${css(styles.noFocus)}`}
          fill={BLUE_LIGHTEST}
          fillOpacity={0.7}
          x={xFn(regionStart)}
          y={zoomedIn ? -5 : 0}
          width={xFn(regionEnd) - xFn(regionStart)}
          height={zoomedIn ? 15 : 5}
          {...getTestProps({
            component: 'AntibodyNumbering',
            element: 'region',
            key: String(region.start),
          })}
          data-amp-safe={false}
        />
      </Tooltip>
      {zoomedIn &&
        range(firstResidue, endResidue - 1).map((residueIndex) => (
          <AntibodyNumber
            key={residueIndex}
            value={numbering.labels[residueIndex - numbering.start]!}
            gappedIndex={toSliceIndex(residueIndex)}
            {...{xFn, layout}}
          />
        ))}
    </>
  );
}

export interface TrackProps {
  layout: AntibodyNumberingLayout;
  numberings: ReadonlyArray<ResidueNumbering>;
  onAlignment: boolean;
  // The residues drawn on the slice, with gaps on an alignment
  residues: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

/**
 * Displays antibody numbers above the numbered residues of the slice. Each run of consecutive numbered
 * residues gets its own region, so residues with no number show as gaps. At wider zoom levels, only
 * shows the regions, with a tooltip, in place of individual numbers.
 */
export function AntibodyNumberingTrack({
  layout,
  numberings,
  onAlignment,
  residues,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
}: TrackProps): JSX.Element {
  assert(onAlignment ? sliceStart === 0 : true, 'Alignments only have one slice, so slice must start at 0');
  const realToGapped = useMemo(() => getRealToGapMap(residues), [residues]);

  return (
    <ComponentWrapper
      layout={layout}
      shouldStopMouseDownPropagation={false}
      dataTestAttrs={{component: 'AntibodyNumbering', element: 'root'}}
    >
      <g fontSize={layout.type.fontSize} className={css(styles.noFocus)}>
        {numberings.flatMap((numbering) =>
          getNumberedRegions(numbering).map((region) => (
            <AntibodyNumberedRegion
              key={region.start}
              {...{
                layout,
                numbering,
                onAlignment,
                realToGapped,
                region,
                setSelection,
                sliceStart,
                sliceEnd,
                xFn,
              }}
            />
          ))
        )}
      </g>
    </ComponentWrapper>
  );
}

export interface Props {
  layout: AntibodyNumberingLayout;
  onAlignment: boolean;
  redraw: (boxHeight: number) => void;
  residues: string;
  scheme: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

/**
 * Displays the antibody numbering ANARCI computes for the residues, in the given scheme.
 */
export default function AntibodyNumberingSlice({
  layout,
  onAlignment,
  redraw,
  residues,
  scheme,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
}: Props): JSX.Element | null {
  const boxHeight = Type.heights[layout.type.fontSize] + layout.vMargin;
  const nonGappedResidues = removeGaps(residues);
  const [numberings, setNumberings] = useState<Array<ValidNumberingResult> | null>(null);
  useEffect(() => {
    const fetchNumbering = async (): Promise<void> => {
      const [response] = await AntibodyAnalysisStore.getNumbering(
        [nonGappedResidues],
        scheme.toLowerCase() as NumberingScheme
      );
      setNumberings(response);
      redraw(response ? boxHeight : 0);
    };
    fetchNumbering();
  }, [boxHeight, nonGappedResidues, scheme, redraw]);

  if (!numberings) {
    return null;
  }

  return (
    <AntibodyNumberingTrack
      numberings={numberings.map((numbering) => ({
        start: numbering.start,
        labels: numbering.indices,
        summary: getMetadataSummary(numbering),
      }))}
      {...{layout, onAlignment, residues, setSelection, sliceStart, sliceEnd, xFn}}
    />
  );
}

const styles = StyleSheet.create({
  noFocus: {
    ':focus': {
      outline: 'none',
    },
  },
});
